import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Convert a 1-based column number to an Excel column letter (1=A, 26=Z, 27=AA, …).
 */
function colNumToLetter(col: number): string {
    let result = '';
    let n = col;
    while (n > 0) {
        const rem = (n - 1) % 26;
        result = String.fromCharCode(65 + rem) + result;
        n = Math.floor((n - 1) / 26);
    }
    return result;
}

/**
 * Normalize a value for comparison: numbers stay numbers, strings are
 * lower-cased for case-insensitive matching (Excel MATCH/VLOOKUP behaviour).
 */
function normalizeForMatch(v: FormulaValue): number | string | boolean | null {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return v;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v.toLowerCase();
    if (Array.isArray(v)) return v.length > 0 ? v[0] : null;
    return null;
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export function registerLookupFunctions(registry: FunctionRegistry): void {
    // ADDRESS(row, col, absNum?, a1?, sheetText?)
    registry.register('ADDRESS', (...args: FormulaValue[]) => {
        const row = Math.trunc(toNum(args[0]));
        const col = Math.trunc(toNum(args[1]));
        const absNum = args[2] !== undefined ? Math.trunc(toNum(args[2])) : 1;
        // args[3] (a1-style) is always treated as TRUE in this engine
        const sheetText = args[4] !== undefined && toStr(args[4]) !== '' ? toStr(args[4]) : '';

        if (row < 1) throw new FormulaError(FormulaErrorType.VALUE, 'ADDRESS: row must be >= 1');
        if (col < 1) throw new FormulaError(FormulaErrorType.VALUE, 'ADDRESS: col must be >= 1');

        const colLetter = colNumToLetter(col);

        let address: string;
        switch (absNum) {
            case 1: // $A$1 – absolute row and column
                address = `$${colLetter}$${row}`;
                break;
            case 2: // A$1 – absolute row, relative column
                address = `${colLetter}$${row}`;
                break;
            case 3: // $A1 – relative row, absolute column
                address = `$${colLetter}${row}`;
                break;
            case 4: // A1 – relative row and column
                address = `${colLetter}${row}`;
                break;
            default:
                throw new FormulaError(FormulaErrorType.VALUE, 'ADDRESS: absNum must be 1–4');
        }

        if (sheetText !== '') {
            return `${sheetText}!${address}`;
        }
        return address;
    });

    // CHOOSE(indexNum, value1, value2, ...)
    registry.register('CHOOSE', (...args: FormulaValue[]) => {
        if (args.length < 2) throw new FormulaError(FormulaErrorType.VALUE, 'CHOOSE: requires at least 2 arguments');
        const idx = Math.trunc(toNum(args[0]));
        if (idx < 1 || idx > args.length - 1) {
            throw new FormulaError(FormulaErrorType.VALUE, `CHOOSE: index ${idx} out of range`);
        }
        return args[idx] ?? null;
    });

    // COLUMN(reference?) – without cell references, return 1
    registry.register('COLUMN', (...args: FormulaValue[]) => {
        // In this engine there are no cell references; return 1 as default
        void args;
        return 1;
    });

    // COLUMNS(array) – return number of columns; for a 1-D array, that is 1
    registry.register('COLUMNS', (...args: FormulaValue[]) => {
        // All arrays in this engine are 1-D (a single row), so columns = 1
        void args;
        return 1;
    });

    // FORMULATEXT(reference) – always #N/A in this engine (no formula cells)
    registry.register('FORMULATEXT', (..._args: FormulaValue[]) => {
        return FormulaErrorType.NA;
    });

    // HLOOKUP(lookupValue, tableArray, rowIndex, rangeLookup?)
    // tableArray is treated as a 1-D array (single row).
    // rowIndex is expected to be 1 for a single-row array.
    registry.register('HLOOKUP', (...args: FormulaValue[]) => {
        if (args.length < 3) throw new FormulaError(FormulaErrorType.VALUE, 'HLOOKUP: requires at least 3 arguments');

        const lookupValue = args[0];
        const tableArray = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];
        const rowIndex = Math.trunc(toNum(args[2]));
        const rangeLookup = args[3] !== undefined ? Boolean(args[3]) : true;

        if (rowIndex < 1) throw new FormulaError(FormulaErrorType.VALUE, 'HLOOKUP: rowIndex must be >= 1');

        const needle = normalizeForMatch(lookupValue);

        if (rangeLookup) {
            // Approximate match: find largest value <= lookupValue (array must be sorted ascending)
            let matchIdx = -1;
            for (let i = 0; i < tableArray.length; i++) {
                const candidate = normalizeForMatch(tableArray[i]);
                if (candidate !== null && candidate <= (needle as number | string | boolean)) {
                    matchIdx = i;
                } else {
                    break;
                }
            }
            if (matchIdx === -1) throw new FormulaError(FormulaErrorType.NA, 'HLOOKUP: no match found');
            return tableArray[matchIdx] ?? null;
        } else {
            // Exact match
            for (let i = 0; i < tableArray.length; i++) {
                if (normalizeForMatch(tableArray[i]) === needle) {
                    return tableArray[i] ?? null;
                }
            }
            throw new FormulaError(FormulaErrorType.NA, 'HLOOKUP: no match found');
        }
    });

    // HYPERLINK(url, friendlyName?)
    registry.register('HYPERLINK', (...args: FormulaValue[]) => {
        if (args.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'HYPERLINK: requires at least 1 argument');
        if (args[1] !== undefined && args[1] !== null && toStr(args[1]) !== '') {
            return toStr(args[1]);
        }
        return toStr(args[0]);
    });

    // INDEX(array, rowNum, colNum?)
    // For 1-D arrays, rowNum selects the element (1-based). colNum is ignored.
    registry.register('INDEX', (...args: FormulaValue[]) => {
        if (args.length < 2) throw new FormulaError(FormulaErrorType.VALUE, 'INDEX: requires at least 2 arguments');

        const arr = Array.isArray(args[0]) ? args[0] : [toNum(args[0])];
        const rowNum = Math.trunc(toNum(args[1]));

        if (rowNum < 1 || rowNum > arr.length) {
            throw new FormulaError(FormulaErrorType.REF, `INDEX: row ${rowNum} out of bounds`);
        }

        return arr[rowNum - 1] ?? null;
    });

    // MATCH(lookupValue, lookupArray, matchType?)
    // matchType: 1 = less-than-or-equal (default, sorted asc)
    //            0 = exact
    //           -1 = greater-than-or-equal (sorted desc)
    registry.register('MATCH', (...args: FormulaValue[]) => {
        if (args.length < 2) throw new FormulaError(FormulaErrorType.VALUE, 'MATCH: requires at least 2 arguments');

        const lookupValue = args[0];
        const lookupArray = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];
        const matchType = args[2] !== undefined ? Math.trunc(toNum(args[2])) : 1;

        const needle = normalizeForMatch(lookupValue);

        if (matchType === 0) {
            // Exact match (case-insensitive for strings)
            for (let i = 0; i < lookupArray.length; i++) {
                if (normalizeForMatch(lookupArray[i]) === needle) {
                    return i + 1;
                }
            }
            throw new FormulaError(FormulaErrorType.NA, 'MATCH: no exact match found');
        }

        if (matchType === 1) {
            // Largest value <= lookupValue in a sorted-ascending array
            let matchIdx = -1;
            for (let i = 0; i < lookupArray.length; i++) {
                const candidate = normalizeForMatch(lookupArray[i]);
                if (candidate !== null && candidate <= (needle as number | string | boolean)) {
                    matchIdx = i;
                } else {
                    break;
                }
            }
            if (matchIdx === -1) throw new FormulaError(FormulaErrorType.NA, 'MATCH: no match found');
            return matchIdx + 1;
        }

        if (matchType === -1) {
            // Smallest value >= lookupValue in a sorted-descending array
            for (let i = 0; i < lookupArray.length; i++) {
                const candidate = normalizeForMatch(lookupArray[i]);
                if (candidate !== null && candidate <= (needle as number | string | boolean)) {
                    return i + 1;
                }
            }
            throw new FormulaError(FormulaErrorType.NA, 'MATCH: no match found');
        }

        throw new FormulaError(FormulaErrorType.VALUE, 'MATCH: matchType must be -1, 0, or 1');
    });

    // OFFSET(reference, rows, cols, height?, width?)
    // Without real cell references this cannot be meaningfully evaluated; return 0.
    registry.register('OFFSET', (..._args: FormulaValue[]) => {
        return 0;
    });

    // ROW(reference?) – always 1 in this engine
    registry.register('ROW', (..._args: FormulaValue[]) => {
        return 1;
    });

    // ROWS(array)
    registry.register('ROWS', (...args: FormulaValue[]) => {
        if (args[0] !== undefined && Array.isArray(args[0])) {
            return (args[0]).length;
        }
        return 1;
    });

    // VLOOKUP(lookupValue, tableArray, colIndex, rangeLookup?)
    // tableArray is treated as a 1-D array (single column).
    // colIndex must be 1 for a single-column array (returns the found value itself).
    registry.register('VLOOKUP', (...args: FormulaValue[]) => {
        if (args.length < 3) throw new FormulaError(FormulaErrorType.VALUE, 'VLOOKUP: requires at least 3 arguments');

        const lookupValue = args[0];
        const tableArray = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];
        const colIndex = Math.trunc(toNum(args[2]));
        const rangeLookup = args[3] !== undefined ? Boolean(args[3]) : true;

        if (colIndex < 1) throw new FormulaError(FormulaErrorType.VALUE, 'VLOOKUP: colIndex must be >= 1');

        const needle = normalizeForMatch(lookupValue);

        if (rangeLookup) {
            // Approximate match: largest value <= lookupValue (sorted ascending)
            let matchIdx = -1;
            for (let i = 0; i < tableArray.length; i++) {
                const candidate = normalizeForMatch(tableArray[i]);
                if (candidate !== null && candidate <= (needle as number | string | boolean)) {
                    matchIdx = i;
                } else {
                    break;
                }
            }
            if (matchIdx === -1) throw new FormulaError(FormulaErrorType.NA, 'VLOOKUP: no match found');
            return tableArray[matchIdx] ?? null;
        } else {
            // Exact match
            for (let i = 0; i < tableArray.length; i++) {
                if (normalizeForMatch(tableArray[i]) === needle) {
                    return tableArray[i] ?? null;
                }
            }
            throw new FormulaError(FormulaErrorType.NA, 'VLOOKUP: no match found');
        }
    });

    // XLOOKUP(lookupValue, lookupArray, returnArray, ifNotFound?, matchMode?, searchMode?)
    // matchMode: 0 exact (default), -1 exact or next smaller, 1 exact or next larger, 2 wildcard
    // searchMode: 1 first-to-last (default), -1 last-to-first, 2 binary asc, -2 binary desc
    registry.register('XLOOKUP', (...args: FormulaValue[]) => {
        if (args.length < 3) throw new FormulaError(FormulaErrorType.VALUE, 'XLOOKUP: requires at least 3 arguments');

        const lookupValue = args[0];
        const lookupArray = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];
        const returnArray = Array.isArray(args[2]) ? args[2] : [toNum(args[2])];
        const ifNotFound: FormulaValue = args[3] !== undefined ? args[3] : null;
        const matchMode = args[4] !== undefined ? Math.trunc(toNum(args[4])) : 0;
        const searchMode = args[5] !== undefined ? Math.trunc(toNum(args[5])) : 1;

        if (lookupArray.length !== returnArray.length) {
            throw new FormulaError(FormulaErrorType.VALUE, 'XLOOKUP: lookupArray and returnArray must have the same length');
        }

        const needle = normalizeForMatch(lookupValue);

        // Build iteration order based on searchMode
        const indices = Array.from({ length: lookupArray.length }, (_, i) => i);
        if (searchMode === -1) indices.reverse();

        // Find match
        let foundIdx = -1;

        if (matchMode === 0 || matchMode === 2) {
            // Exact match (matchMode 2 = wildcard, treated as exact in this engine)
            for (const i of indices) {
                if (normalizeForMatch(lookupArray[i]) === needle) {
                    foundIdx = i;
                    break;
                }
            }
        } else if (matchMode === -1) {
            // Exact or next smaller
            let bestVal: number | string | boolean | null = null;
            for (let i = 0; i < lookupArray.length; i++) {
                const candidate = normalizeForMatch(lookupArray[i]);
                if (candidate === needle) { foundIdx = i; break; }
                if (candidate !== null && candidate <= (needle as number | string | boolean)) {
                    if (bestVal === null || candidate > bestVal) {
                        bestVal = candidate;
                        foundIdx = i;
                    }
                }
            }
        } else if (matchMode === 1) {
            // Exact or next larger
            let bestVal: number | string | boolean | null = null;
            for (let i = 0; i < lookupArray.length; i++) {
                const candidate = normalizeForMatch(lookupArray[i]);
                if (candidate === needle) { foundIdx = i; break; }
                if (candidate !== null && candidate >= (needle as number | string | boolean)) {
                    if (bestVal === null || candidate < bestVal) {
                        bestVal = candidate;
                        foundIdx = i;
                    }
                }
            }
        }

        if (foundIdx === -1) {
            if (ifNotFound !== null && ifNotFound !== undefined) return ifNotFound;
            throw new FormulaError(FormulaErrorType.NA, 'XLOOKUP: no match found');
        }

        return returnArray[foundIdx] ?? null;
    });
}
